import { useGetMillwrightByIdQuery } from '@/apis/millwrights/millwrightsApi';
import { BackButton } from '@/components';
import { SPINNER_SIZE } from '@/constants';
import { OUTAGE_ROUTE } from '@/pages/Outages/routes';
import {
  Avatar,
  Icon,
  LoadingSpinner,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { generatePath, useParams } from 'react-router';
import { MILLWRIGHTS_ROUTE } from '@/pages/Millwrights/routes';
import { useGetSkillsByMillwrightIdQuery } from '@/apis/skills/skillsApi';
import { Skill } from '@/apis/skills/types/skillsApi';
import {
  MillwrightDetails,
  MillwrightEvaluations,
  MillwrightProfile,
  MillwrightTasks,
} from '@/pages/Millwrights/[ID]/components';

import '@/pages/Millwrights/[ID]/styles.scss';

type MillwrightPageParams = {
  outageId: string;
  millwrightId: string;
};

const sortSkillsByExpirationDate = (a: Skill, b: Skill) => {
  const now = new Date();

  const aExp = a.expirationDate ? new Date(a.expirationDate) : null;
  const bExp = b.expirationDate ? new Date(b.expirationDate) : null;

  const aExpired = aExp ? aExp < now : false;
  const bExpired = bExp ? bExp < now : false;

  if (aExpired && !bExpired) return -1;
  if (!aExpired && bExpired) return 1;

  if (aExp && bExp) {
    return bExp.getTime() - aExp.getTime();
  }

  if (aExp && !bExp) return -1;
  if (!aExp && bExp) return 1;

  return 0;
};

export const MillwrightPage = () => {
  const { outageId, millwrightId } = useParams<MillwrightPageParams>();
  const millwrightID = Number(millwrightId);
  const outageID = Number(outageId);

  const { data: millwright, isLoading: isLoadingMillwright } =
    useGetMillwrightByIdQuery(
      { empId: millwrightID, outageId: outageID },
      {
        skip: !millwrightID,
      },
    );

  const { data: skillsData, isLoading: isLoadingSkills } =
    useGetSkillsByMillwrightIdQuery(
      { empId: millwrightID },
      {
        skip: !millwrightID,
      },
    );

  const skills = skillsData?.results ?? [];
  const sortedSkills = [...skills]?.sort(sortSkillsByExpirationDate);
  const isLoading = isLoadingMillwright || isLoadingSkills;

  if (isLoading) {
    return (
      <div className="outages-page-spinner">
        <LoadingSpinner
          text="Loading millwright..."
          size={SPINNER_SIZE}
          color="var(--color-primary)"
          align="center"
          tag="textdefault"
          inline
        />
      </div>
    );
  }

  if (!millwright) return null;

  return (
    <div className="millwright-page">
      <div className="millwright-page__header">
        <Avatar
          {...(millwright.hasPhoto ? { imageURL: millwright.photoData } : {})}
        />
        <Typography color="var(--color-black)" tag="h3">
          {millwright.fullName}
        </Typography>
        <BackButton
          className="millwright-page__header-back-button"
          url={
            outageId
              ? generatePath(OUTAGE_ROUTE, { outageId })
              : MILLWRIGHTS_ROUTE
          }
        />
      </div>
      <MillwrightDetails
        millwright={millwright}
        skills={sortedSkills}
        outageId={outageId ?? ''}
      />
      <Stack inline verticalAlign="center">
        <Icon iconSize={1} color="var(--color-default)" icon="IconArrowDown" />
      </Stack>
      <MillwrightProfile millwrightId={millwright.empId} outageId={outageID} />
      <Stack inline verticalAlign="center">
        <Icon iconSize={1} color="var(--color-default)" icon="IconArrowDown" />
      </Stack>
      <MillwrightTasks millwrightId={millwright.empId} outageId={outageID} />
      <Stack inline verticalAlign="center">
        <Icon iconSize={1} color="var(--color-default)" icon="IconArrowDown" />
      </Stack>
      <MillwrightEvaluations
        millwrightId={millwright.empId}
        outageId={outageID}
      />
    </div>
  );
};
